export const TREND_MONTHS = 6;

// Built by hand rather than via toISOString(), which would shift a day across a
// UTC boundary.
const iso = (year, month, day) => `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

const daysInMonth = (year, month) => new Date(year, month, 0).getDate();

/**
 * The previous TREND_MONTHS full months relative to `endDate`. The current
 * partial month is excluded — incomplete data always reads as a usage collapse.
 *
 * Returns null when there is no usable end date, so callers render empty rather
 * than query an unbounded range.
 */
export function trendWindow(endDate, months = TREND_MONTHS) {
  if (!endDate) return null;

  const parsed = String(endDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!parsed) return null;

  const year = Number(parsed[1]);
  const month = Number(parsed[2]);

  // 0-based month indexes, so the arithmetic carries across years without a
  // January special case.
  const lastFullIndex = year * 12 + (month - 1) - 1;
  const firstIndex = lastFullIndex - (months - 1);

  const endYear = Math.floor(lastFullIndex / 12);
  const endMonth = (lastFullIndex % 12) + 1;
  const startYear = Math.floor(firstIndex / 12);
  const startMonth = (firstIndex % 12) + 1;

  return {
    start: iso(startYear, startMonth, 1),
    end: iso(endYear, endMonth, daysInMonth(endYear, endMonth)),
  };
}

/**
 * The window's months oldest-first. The chart takes its axis from here rather
 * than from the data, which omits empty (month, formulation) pairs.
 */
export function trendMonths(endDate, months = TREND_MONTHS) {
  const window = trendWindow(endDate, months);
  if (!window) return [];

  const [startYear, startMonth] = window.start.split('-').map(Number);
  const firstIndex = startYear * 12 + (startMonth - 1);

  return Array.from({ length: months }, (_, offset) => {
    const index = firstIndex + offset;
    return { year: Math.floor(index / 12), month: (index % 12) + 1 };
  });
}

export default trendWindow;
